import * as THREE from 'three';
import type { CanonicalEntity } from '@/shared/api/types';
import { kelvinToColor } from './lib/light-rigs';
import { enableEmissiveBloomLayer } from './lib/selectiveBloom';

/**
 * ВОЛНА P — «Премиум-примитивы аксессуаров» (PHAETON-PREMIUM).
 *
 * Render-only архетипы нишевых аксессуаров: штанга, корзина, посудосушитель,
 * карго, LED-подсветка ниши.  Эталон картинки — демо-репродуктор владельца
 * `/tmp/basket-demo/index.html` (three 0.166, RoomEnvironment, bloom 0.28/0.92,
 * exposure 0.82): проволочный каркас из ЦИЛИНДРОВ (не боксов) с хром-PBR.
 *
 * ПАРАМЕТРИЧНОСТЬ.  Архетип — это код, а не модель: каждый билдер получает
 * только роль и AABB кита (объединение боксов физических частей из плана) и
 * растягивается по нему.  Число проволок, радиусы и ярусы вычисляются из
 * габаритов, поэтому корзина 400 мм и 560 мм получают разную плотность
 * каркаса при одном и том же коде.  Ничего не импортируется, бандл/BOM/смета
 * не затрагиваются: физические части остаются основой, архетип их «одевает».
 *
 * КАЛИБРОВКА БЛИКА.  Прод-сцена ярче демо-эталона (toneMappingExposure 1.12
 * против 0.82, environmentIntensity 1.0 против 0.55), а её photoreal-bloom
 * мягче (0.2/0.88 против 0.28/0.92).  Глобальные настройки сцены не трогаем:
 * пересвет хрома гасится НА МАТЕРИАЛАХ архетипа —roughness поднят до 0.22/0.34
 * и envMapIntensity 0.62/0.7, что даёт ту же плотность блика, что и демо.
 */

/** Роль кита (`domain.accessoryKitMember.accessoryKind`), имеющая архетип. */
export type AccessoryArchetypeRole = 'rod' | 'basket' | 'dish_dryer' | 'cargo' | 'led';

export type AccessoryArchetypeBounds = {
  /** AABB в мм, в координатах сцены. */
  min: [number, number, number];
  max: [number, number, number];
};

export type AccessoryArchetypeParams = {
  /** Цветовая температура LED-ленты, K (для role=led). */
  colorTempK?: number;
};

type Vec3 = [number, number, number];

const ARCHETYPE_ROLES: ReadonlySet<string> = new Set(['rod', 'basket', 'dish_dryer', 'cargo', 'led']);

/** Кит меньше этого числа проволок не стоит собирать — фолбэк на боксы. */
const MIN_KIT_SPAN_MM = 60;

export function accessoryArchetypeRoleForKind(kind: unknown): AccessoryArchetypeRole | null {
  const clean = String(kind ?? '').trim().toLowerCase();
  return ARCHETYPE_ROLES.has(clean) ? clean as AccessoryArchetypeRole : null;
}

// ——— материалы: модульные синглтоны, переживают пересборки сцены ———

let materialsSingleton: AccessoryArchetypeMaterials | null = null;
const ledGlowByTemp = new Map<number, THREE.MeshStandardMaterial>();

export type AccessoryArchetypeMaterials = {
  chrome: THREE.MeshStandardMaterial;
  steel: THREE.MeshStandardMaterial;
  ledGlow: THREE.MeshStandardMaterial;
};

/**
 * Свечение LED: базовый синглтон на 4000 K (демо-эталон) + по клона-кэшу на
 * каждую отличную температуру, чтобы две ленты 3000 K и 5000 K в одной
 * сцене не перекрашивали друг друга.  Клоны тоже живут в модуле — это всё
 * ещё кэш, а не «новый материал на каждый кит».
 */
export function ledGlowForTemp(colorTempK: number): THREE.MeshStandardMaterial {
  const base = accessoryArchetypeMaterials().ledGlow;
  const temp = Math.max(1800, Math.min(6500, Math.round(colorTempK)));
  if (temp === 4000) return base;
  let mat = ledGlowByTemp.get(temp);
  if (!mat) {
    mat = base.clone();
    mat.name = `accessory-archetype-led-glow-${temp}k`;
    const tint = kelvinToColor(temp);
    mat.color.copy(tint).lerp(new THREE.Color(0xfff3e0), 0.4);
    mat.emissive.copy(tint).lerp(new THREE.Color(0xffdfae), 0.35);
    ledGlowByTemp.set(temp, mat);
  }
  return mat;
}

/**
 * Хром/сталь — один экземпляр на модуль.  Морфы режимов рендера
 * (`FurnitureRenderObject.applyMaterialMode`) пишут состояние в материал
 * всей группы архетипов одинаково, так что общий материал без клонов
 * безопасен и экономен; dispose-цикл сцены текстур у них не находит.
 * Свечение LED кэшируется по цветовой температуре (см. ledGlowForTemp).
 */
export function accessoryArchetypeMaterials(): AccessoryArchetypeMaterials {
  if (!materialsSingleton) {
    materialsSingleton = {
      // Демо: color 0xf2f4f6, roughness 0.18, metalness 1.0.  Прод-сцена
      // ярче — roughness/envMapIntensity придержаны (см. шапку модуля).
      chrome: new THREE.MeshStandardMaterial({
        color: 0xf2f4f6,
        roughness: 0.22,
        metalness: 1.0,
        envMapIntensity: 0.62,
      }),
      steel: new THREE.MeshStandardMaterial({
        color: 0xb8bcc2,
        roughness: 0.34,
        metalness: 1.0,
        envMapIntensity: 0.7,
      }),
      ledGlow: new THREE.MeshStandardMaterial({
        color: 0xfff3e0,
        emissive: 0xffdfae,
        emissiveIntensity: 2.4,
        roughness: 0.4,
        metalness: 0.0,
      }),
    };
    materialsSingleton.chrome.name = 'accessory-archetype-chrome';
    materialsSingleton.steel.name = 'accessory-archetype-steel';
    materialsSingleton.ledGlow.name = 'accessory-archetype-led-glow';
  }
  return materialsSingleton;
}

/** Тестовый хук: уронить синглтоны (в проде не вызывается). */
export function resetAccessoryArchetypeMaterialsForTest(): void {
  materialsSingleton = null;
  ledGlowByTemp.clear();
}

// ——— геометрия: локальный дедуп на сборку + явный dispose ———

class WireGeometryPool {
  private readonly cache = new Map<string, THREE.BufferGeometry>();

  /** Цилиндр радиуса r и длины len, ось — axis.  Одинаковые прутки делят геометрию. */
  wire(axis: 'x' | 'y' | 'z', radius: number, length: number, segments: number): THREE.BufferGeometry {
    const key = `cyl|${axis}|${radius.toFixed(2)}|${length.toFixed(1)}|${segments}`;
    let geom = this.cache.get(key);
    if (!geom) {
      geom = new THREE.CylinderGeometry(radius, radius, Math.max(1, length), segments);
      if (axis === 'x') geom.rotateZ(Math.PI / 2);
      else if (axis === 'z') geom.rotateX(Math.PI / 2);
      this.cache.set(key, geom);
    }
    return geom;
  }

  torus(radius: number, tube: number, arc: number): THREE.BufferGeometry {
    const key = `tor|${radius.toFixed(2)}|${tube.toFixed(2)}`;
    let geom = this.cache.get(key);
    if (!geom) {
      geom = new THREE.TorusGeometry(radius, tube, 10, 24, arc);
      this.cache.set(key, geom);
    }
    return geom;
  }

  box(sx: number, sy: number, sz: number): THREE.BufferGeometry {
    const key = `box|${sx.toFixed(1)}|${sy.toFixed(1)}|${sz.toFixed(1)}`;
    let geom = this.cache.get(key);
    if (!geom) {
      geom = new THREE.BoxGeometry(Math.max(1, sx), Math.max(1, sy), Math.max(1, sz));
      this.cache.set(key, geom);
    }
    return geom;
  }
}

/**
 * Освободить геометрию архетипа при пересборке.  Материалы-синглтоны не
 * трогаем (общие для всех китов), геометрия же принадлежит этой сборке.
 * Общий dispose-цикл сцены (`disposeObject`) и так вызывает dispose на
 * мешах сцены — хелтор нужен для отвязанных/тестовых групп.
 */
export function disposeAccessoryArchetypeGeometry(root: THREE.Object3D): void {
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    (mesh.geometry as { disposeBoundsTree?: () => void } & THREE.BufferGeometry)
      .disposeBoundsTree?.();
    mesh.geometry.dispose();
  });
}

// ——— общие примитивы каркаса ———

type Frame = {
  group: THREE.Group;
  /** Локальный центр AABB относительно position группы. */
  center: Vec3;
  size: Vec3;
};

function boundsFrame(bounds: AccessoryArchetypeBounds): Frame {
  const min = bounds.min;
  const max = bounds.max;
  const size: Vec3 = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
  const center: Vec3 = [
    (min[0] + max[0]) / 2,
    (min[1] + max[1]) / 2,
    (min[2] + max[2]) / 2,
  ];
  const group = new THREE.Group();
  group.position.set(center[0], center[1], center[2]);
  return { group, center, size };
}

function wireMesh(geom: THREE.BufferGeometry, mat: THREE.Material): THREE.Mesh {
  const mesh = new THREE.Mesh(geom, mat);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

// ——— штанга ———

/**
 * Цилиндр по ширине ниши + круглые фланцы/крышки.  Радиус — из AABB кита
 * (сами фланцы входят в объединение, поэтому шток читается из полной высоты
 * как 0.5·min(h,d), фланец — почти во всю высоту, как в демо 26/48).
 */
export function rodArchetype(bounds: AccessoryArchetypeBounds, pool: WireGeometryPool): THREE.Group {
  const { group, size } = boundsFrame(bounds);
  const mats = accessoryArchetypeMaterials();
  const w = Math.max(MIN_KIT_SPAN_MM, size[0]);
  const profile = Math.max(10, Math.min(size[1], size[2]));
  const flangeR = profile * 0.5 + 2;
  const rodR = flangeR * 0.5;
  const flangeH = Math.max(8, Math.min(14, w * 0.02));
  const capH = Math.max(3, flangeH * 0.4);
  const rodLen = Math.max(MIN_KIT_SPAN_MM, w - 2 * flangeH - 4);

  group.add(wireMesh(pool.wire('x', rodR, rodLen, 24), mats.chrome));
  for (const side of [-1, 1]) {
    const flange = wireMesh(pool.wire('x', flangeR, flangeH, 24), mats.steel);
    flange.position.x = side * (w / 2 - flangeH / 2 - 2);
    group.add(flange);
    const cap = wireMesh(pool.wire('x', flangeR * 0.77, capH, 24), mats.chrome);
    cap.position.x = side * (w / 2 - capH / 2);
    group.add(cap);
  }
  return group;
}

// ——— корзина ———

/**
 * Проволочный каркас из цилиндров: рамки дна/верха, вертикальные стойки,
 * поперечины с шагом, ручка-тор.  Плотность проволок масштабируется от
 * габаритов (шаг ~50–60 мм), поэтому корзина «дышит» вместе с нишей.
 * Порт `basketNew` из демо-эталона, размеры параметризованы.
 */
export function basketArchetype(bounds: AccessoryArchetypeBounds, pool: WireGeometryPool): THREE.Group {
  const { group, size } = boundsFrame(bounds);
  const mats = accessoryArchetypeMaterials();
  const w = Math.max(80, size[0]);
  const d = Math.max(60, size[2]);
  const h = Math.max(50, size[1]);
  const r2 = Math.max(3, Math.min(5, Math.min(w, d) * 0.009));
  const r = r2 * 0.8;
  const iw = w - 2 * r2;
  const id = d - 2 * r2;
  const ih = h - 4;
  const seg = 14;

  const put = (mesh: THREE.Mesh, x: number, y: number, z: number) => {
    mesh.position.set(x, y - h / 2, z);
    group.add(mesh);
  };

  // рамка уровня: длинные стороны — по два составных прутка (стык как у реальной корзины)
  const frame = (y: number) => {
    for (const side of [-1, 1]) {
      put(wireMesh(pool.wire('x', r2, iw / 2, seg), mats.chrome), side * (iw / 4), y, -d / 2 + r2);
      put(wireMesh(pool.wire('x', r2, iw / 2, seg), mats.chrome), side * (iw / 4), y, d / 2 - r2);
      put(wireMesh(pool.wire('z', r2, id / 2, seg), mats.chrome), -w / 2 + r2, y, side * (id / 4));
      put(wireMesh(pool.wire('z', r2, id / 2, seg), mats.chrome), w / 2 - r2, y, side * (id / 4));
    }
    const nCross = Math.max(4, Math.round(id / 50));
    for (let i = 0; i <= nCross; i++) {
      const z = -d / 2 + r2 + (i * id) / nCross;
      put(wireMesh(pool.wire('x', r, iw, seg), mats.steel), 0, y, z);
    }
  };
  frame(2);
  frame(h - 2);

  // вертикали: углы + стенки
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      put(wireMesh(pool.wire('y', r2, ih, seg), mats.chrome), sx * (w / 2 - r2), 0, sz * (d / 2 - r2));
    }
  }
  const nSide = Math.max(2, Math.round(id / 60) - 1);
  for (let i = 1; i <= nSide; i++) {
    const z = -d / 2 + r2 + (i * id) / (nSide + 1);
    for (const sx of [-1, 1]) {
      put(wireMesh(pool.wire('y', r, ih, seg), mats.steel), sx * (w / 2 - r2), 0, z);
    }
  }
  const nMid = Math.max(1, Math.round(iw / 130));
  for (let i = 1; i <= nMid; i++) {
    const x = -w / 2 + r2 + (i * iw) / (nMid + 1);
    put(wireMesh(pool.wire('y', r, ih, seg), mats.steel), x, 0, 0);
  }

  // ручка — труба с загибом (полутор на передней кромке, перед камеры +Z)
  const handleR = Math.max(22, Math.min(44, w * 0.14));
  const handle = wireMesh(pool.torus(handleR, r2, Math.PI), mats.chrome);
  handle.position.set(0, h / 2 - 2, d / 2 - r2);
  handle.rotation.y = Math.PI / 2;
  group.add(handle);
  return group;
}

// ——— посудосушитель ———

/**
 * До двух ярусов хром-реек в несущих балках: рейки поперёк глубины,
 * балки — вдоль ширины.  Второй ярус появляется, когда высота кита
 * позволяет (как `tiers` на бэкенде), раскладка ярусов — от полной высоты.
 */
export function dryerArchetype(bounds: AccessoryArchetypeBounds, pool: WireGeometryPool): THREE.Group {
  const { group, size } = boundsFrame(bounds);
  const mats = accessoryArchetypeMaterials();
  const w = Math.max(160, size[0]);
  const d = Math.max(120, size[2]);
  const h = Math.max(40, size[1]);
  const tiers = h >= 140 ? 2 : 1;
  const railR = Math.max(3.4, Math.min(5.5, Math.min(w, d) * 0.011));
  const beamR = railR * 1.3;
  const railCount = Math.max(5, Math.min(12, Math.round((w - 48) / 55)));
  const railLen = Math.max(40, d - 14);

  for (let tier = 0; tier < tiers; tier++) {
    const y = -h / 2 + (tier === 0 ? h * 0.16 : h * 0.62);
    const row = new THREE.Group();
    row.position.y = y;
    for (let i = 0; i < railCount; i++) {
      const x = -w / 2 + 24 + (i * (w - 48)) / (railCount - 1);
      const rail = wireMesh(pool.wire('z', railR, railLen, 16), mats.chrome);
      rail.position.x = x;
      row.add(rail);
    }
    for (const side of [-1, 1]) {
      const beam = wireMesh(pool.wire('x', beamR, Math.max(40, w - 40), 16), mats.steel);
      beam.position.z = side * (d / 2 - 10);
      row.add(beam);
    }
    group.add(row);
  }
  return group;
}

// ——— карго ———

/**
 * Колонна рейек: угловые стойки во всю высоту, ярусные рамки с шагом
 * (~140 мм, 3–5 ярусов — бутылочница читается колонной, а не пустой рамой)
 * и ручка-тор сверху.  Как `cargoNew` из демо, расширенный на N ярусов.
 */
export function cargoArchetype(bounds: AccessoryArchetypeBounds, pool: WireGeometryPool): THREE.Group {
  const { group, size } = boundsFrame(bounds);
  const mats = accessoryArchetypeMaterials();
  const w = Math.max(90, size[0]);
  const d = Math.max(90, size[2]);
  const h = Math.max(120, size[1]);
  const r2 = Math.max(3.4, Math.min(5, Math.min(w, d) * 0.011));
  const r = r2 * 0.8;
  const seg = 14;
  const tiers = Math.max(3, Math.min(5, Math.round(h / 140)));
  const iw = w - 2 * r2;
  const id = d - 2 * r2;

  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const post = wireMesh(pool.wire('y', r2, h - 4, seg), mats.chrome);
      post.position.set(sx * (w / 2 - r2), 0, sz * (d / 2 - r2));
      group.add(post);
    }
  }
  for (let tier = 0; tier < tiers; tier++) {
    const y = -h / 2 + 6 + (tier * (h - 12)) / (tiers - 1);
    for (const sz of [-1, 1]) {
      const rail = wireMesh(pool.wire('x', r2, iw, seg), mats.chrome);
      rail.position.set(0, y, sz * (d / 2 - r2));
      group.add(rail);
    }
    for (const sx of [-1, 1]) {
      const rail = wireMesh(pool.wire('z', r2, id, seg), mats.chrome);
      rail.position.set(sx * (w / 2 - r2), y, 0);
      group.add(rail);
    }
    const nCross = Math.max(1, Math.round(iw / 90));
    for (let i = 1; i <= nCross; i++) {
      const x = -w / 2 + r2 + (i * iw) / (nCross + 1);
      const rail = wireMesh(pool.wire('z', r, id, seg), mats.steel);
      rail.position.set(x, y, 0);
      group.add(rail);
    }
  }
  // ручка на верхней передней кромке (перед камеры — +Z)
  const handleR = Math.max(20, Math.min(40, w * 0.16));
  const handle = wireMesh(pool.torus(handleR, r2, Math.PI), mats.chrome);
  handle.position.set(0, h / 2 - 2, d / 2 - r2);
  handle.rotation.y = Math.PI / 2;
  group.add(handle);
  return group;
}

// ——— LED-подсветка ниши ———

/**
 * Эмиссивная полоса + PointLight-пул (11/6 — интенсивности из демо-эталона).
 * Свет льётся ВНИЗ в нишу: кит-лента висит у верхней кромки, пул — под ней.
 * Подсветка рабочей зоны (`under_cabinet_light`) сюда НЕ попадает — её
 * обслуживает `UnderCabinetLightingVisual`, двойной свет исключён ролью.
 */
export function ledArchetype(
  bounds: AccessoryArchetypeBounds,
  pool: WireGeometryPool,
  params?: AccessoryArchetypeParams,
): THREE.Group {
  const { group, size } = boundsFrame(bounds);
  const w = Math.max(MIN_KIT_SPAN_MM, size[0]);
  const colorTempK = Math.max(1800, Math.min(6500, Math.round(Number(params?.colorTempK) || 4000)));
  const tint = kelvinToColor(colorTempK);

  const strip = wireMesh(
    pool.box(w - 4, Math.max(4, Math.min(6, size[1])), Math.max(10, Math.min(16, size[2]))),
    ledGlowForTemp(colorTempK),
  );
  strip.castShadow = false;
  strip.receiveShadow = false;
  enableEmissiveBloomLayer(strip);
  group.add(strip);

  // пул света под лентой: два PointLight (ближний ярче), как в демо
  const pool1 = new THREE.PointLight(tint.clone(), 11, 620, 2);
  pool1.position.set(0, -70, 0);
  pool1.castShadow = false;
  group.add(pool1);
  const pool2 = new THREE.PointLight(tint.clone(), 6, 460, 2);
  pool2.position.set(0, -160, 0);
  pool2.castShadow = false;
  group.add(pool2);
  return group;
}

// ——— фасад билдеров ———

export function buildAccessoryArchetype(input: {
  role: AccessoryArchetypeRole;
  bounds: AccessoryArchetypeBounds;
  params?: AccessoryArchetypeParams;
}): THREE.Group | null {
  const pool = new WireGeometryPool();
  const { role, bounds } = input;
  const span = Math.max(
    bounds.max[0] - bounds.min[0],
    bounds.max[1] - bounds.min[1],
    bounds.max[2] - bounds.min[2],
  );
  if (!Number.isFinite(span) || span < MIN_KIT_SPAN_MM) return null;
  try {
    switch (role) {
      case 'rod': return rodArchetype(bounds, pool);
      case 'basket': return basketArchetype(bounds, pool);
      case 'dish_dryer': return dryerArchetype(bounds, pool);
      case 'cargo': return cargoArchetype(bounds, pool);
      case 'led': return ledArchetype(bounds, pool, input.params);
      default: return null;
    }
  } catch {
    // Фолбэк: любые сомнения — старый рендер боксов.
    return null;
  }
}

// ——— разбор китов из canonical-сущностей ———

export type AccessoryArchetypeKit = {
  role: AccessoryArchetypeRole;
  ownerEntityId: string;
  bounds: AccessoryArchetypeBounds;
  /** Все сущности-участники кита (включая владельца). */
  memberEntityIds: ReadonlySet<string>;
  params: AccessoryArchetypeParams;
};

type KitAccumulator = {
  role: AccessoryArchetypeRole;
  ownerEntityId: string;
  min: Vec3;
  max: Vec3;
  memberEntityIds: Set<string>;
  colorTempK?: number;
};

function recordFrom(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function finiteVec3(value: unknown): Vec3 | null {
  if (!Array.isArray(value) || value.length < 3) return null;
  const out: Vec3 = [0, 0, 0];
  for (let i = 0; i < 3; i += 1) {
    const n = Number(value[i]);
    if (!Number.isFinite(n)) return null;
    out[i] = n;
  }
  return out;
}

/**
 * Собрать архетипные киты из сущностей: участники делят
 * `domain.accessoryKitMember.ownerEntityId`, вид кита — `accessoryKind`.
 * Владелец (биллинговая сущность кита) рисует архетип по объединённому AABB
 * всех участников; участники остаются в сцене как тех-режимный фолбэк.
 */
export function collectAccessoryArchetypeKits(
  entities: readonly CanonicalEntity[],
): Map<string, AccessoryArchetypeKit> {
  const kits = new Map<string, KitAccumulator>();
  const byOwner = new Map<string, CanonicalEntity[]>();
  for (const entity of entities) {
    const member = recordFrom(recordFrom(entity.domain).accessoryKitMember);
    const ownerEntityId = String(member.ownerEntityId ?? '').trim();
    if (!ownerEntityId) continue;
    const role = accessoryArchetypeRoleForKind(member.accessoryKind);
    if (!role) continue;
    const render = entity.render;
    const center = finiteVec3(render?.center);
    const size = finiteVec3(render?.size);
    if (!center || !size) continue;
    let kit = kits.get(ownerEntityId);
    if (!kit) {
      kit = {
        role,
        ownerEntityId,
        min: [Infinity, Infinity, Infinity],
        max: [-Infinity, -Infinity, -Infinity],
        memberEntityIds: new Set<string>(),
      };
      kits.set(ownerEntityId, kit);
    }
    if (kit.role !== role) continue; // разные киты не сливаются
    // Границы участника — только render.aabb (мировой конверт, владелец —
    // бэкенд-поворот).  center ± size/2 здесь врёт: render.size — ЛОКАЛЬНЫЙ
    // размер детали (контракт rotate_entity_render_y), и у модуля с yaw=90°
    // локальный размер вдоль мировой оси X даёт конверт, вылезающий за
    // корпус; на нём архетип строился распухшим и «в воздухе» (замер на
    // buildspec-кухне 05.10: границы кита x −246..560 при нише 0..350).
    // aabb может отсутствовать у боксов без aabb — тогда прежняя оценка.
    const aabb = recordFrom(render?.aabb);
    const aabbMin = finiteVec3(aabb.min as unknown);
    const aabbMax = finiteVec3(aabb.max as unknown);
    for (let i = 0; i < 3; i += 1) {
      const lo = aabbMin && aabbMax ? aabbMin[i] : center[i] - size[i] / 2;
      const hi = aabbMin && aabbMax ? aabbMax[i] : center[i] + size[i] / 2;
      kit.min[i] = Math.min(kit.min[i], lo);
      kit.max[i] = Math.max(kit.max[i], hi);
    }
    kit.memberEntityIds.add(entity.id);
    const bucket = byOwner.get(ownerEntityId) ?? [];
    bucket.push(entity);
    byOwner.set(ownerEntityId, bucket);
  }
  const result = new Map<string, AccessoryArchetypeKit>();
  for (const [ownerEntityId, kit] of kits) {
    // Владелец обязан присутствовать среди участников — иначе архетипу
    // некуда прикрепиться, и кит целиком остаётся на старом рендере.
    if (!kit.memberEntityIds.has(ownerEntityId)) continue;
    const span = Math.max(
      kit.max[0] - kit.min[0],
      kit.max[1] - kit.min[1],
      kit.max[2] - kit.min[2],
    );
    if (!Number.isFinite(span) || span < MIN_KIT_SPAN_MM) continue;
    const owner = (byOwner.get(ownerEntityId) ?? []).find((entity) => entity.id === ownerEntityId);
    // colorTemp приходит render-оверрайдом кита («emissive»-лента) или
    // доменом; отсутствие — норма, берётся демо-дефолт 4000 K.
    const colorTemp = Number(recordFrom(owner?.render).colorTemp)
      || Number(recordFrom(recordFrom(owner?.domain).accessoryKitMember).colorTemp);
    const params: AccessoryArchetypeParams = {};
    if (Number.isFinite(colorTemp) && colorTemp > 0) params.colorTempK = colorTemp;
    result.set(ownerEntityId, {
      role: kit.role,
      ownerEntityId,
      bounds: { min: kit.min, max: kit.max },
      memberEntityIds: kit.memberEntityIds,
      params,
    });
  }
  return result;
}

/** Индекс «сущность → её кит» для O(1) поиска в сборке мешей. */
export function indexAccessoryArchetypeKitsByMember(
  kits: Map<string, AccessoryArchetypeKit>,
): Map<string, AccessoryArchetypeKit> {
  const byMember = new Map<string, AccessoryArchetypeKit>();
  for (const kit of kits.values()) {
    for (const memberId of kit.memberEntityIds) byMember.set(memberId, kit);
  }
  return byMember;
}

// ——— режимная видимость: архетип ↔ боксы-фолбэк ———

/** Пометка внутренней группы-архетипа (видна только в solid). */
export const ACCESSORY_ARCHETYPE_PROXY_FLAG = 'accessoryArchetypeProxy';
/** Пометка группы старого рендера кита (видна во всех режимах, кроме solid). */
export const ACCESSORY_ARCHETYPE_FALLBACK_FLAG = 'accessoryArchetypeFallback';

/**
 * Применить режим к помеченным узлам архетипов.  Вызывается из общего
 * обхода смены режимов (`useRenderModes`): solid/photoreal показывает
 * архетип, техрежимы (wireframe/xray/flat) — старые боксы «как было».
 * Режим берётся целиком, без морф-перехода — как каркас и «плоско».
 */
export function applyAccessoryArchetypeMode(object: THREE.Object3D, mode: string): void {
  const userData = object.userData as Record<string, unknown> | undefined;
  if (!userData) return;
  if (userData[ACCESSORY_ARCHETYPE_PROXY_FLAG] === true) {
    const solid = mode === 'solid';
    object.visible = solid;
    // Рейкастер three не проверяет .visible: скрытый в техрежимах архетип
    // продолжал бы ловить клики поверх видимых боксов участника (P3 аудита
    // волны P). Прецедент тишины — basis-hardware-library.ts.
    object.traverse((child) => {
      const ud = child.userData as Record<string, unknown>;
      if (solid) {
        if (typeof ud.__archetypeRaycast === 'function') {
          child.raycast = ud.__archetypeRaycast as typeof child.raycast;
          delete ud.__archetypeRaycast;
        }
      } else if (typeof ud.__archetypeRaycast === 'undefined') {
        ud.__archetypeRaycast = child.raycast;
        child.raycast = () => undefined;
      }
    });
  } else if (userData[ACCESSORY_ARCHETYPE_FALLBACK_FLAG] === true) {
    object.visible = mode !== 'solid';
  }
}

/** Установить стартовую видимость узлов архетипа по текущему режиму. */
export function initAccessoryArchetypeMode(root: THREE.Object3D, mode: string): void {
  root.traverse((obj) => applyAccessoryArchetypeMode(obj, mode));
}
